import { useEffect } from 'react';
import { useAuth } from '../auth/useAuth';

const API_URL = import.meta.env.VITE_API_URL;
const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';

// ---------------------------------------------------------------------------
// Token Manager (singleton for storing the current ID token)
// ---------------------------------------------------------------------------

let currentToken = null;
let tokenGetterFn = null;

export function setTokenGetter(getterFn) {
  tokenGetterFn = getterFn;
}

export async function getToken() {
  if (BYPASS_AUTH) {
    return null;
  }
  if (tokenGetterFn) {
    try {
      currentToken = await tokenGetterFn();
      return currentToken;
    } catch (err) {
      // The token getter returns null once the session is stale — treat
      // that as an expired session so the SessionExpiredModal can prompt for
      // a re-login instead of the user seeing silent 401s downstream.
      console.error('[apiClient] Failed to get token:', err);
      notifySessionExpired();
      return null;
    }
  }
  return currentToken;
}

// ---------------------------------------------------------------------------
// Session-expired signal (singleton listener; same shape as setTokenGetter)
// ---------------------------------------------------------------------------

let sessionExpiredListener = null;
// Multiple in-flight requests can all 401 at once — dedupe so the listener
// fires exactly once per expiry until it's cleared.
let sessionExpiredFired = false;

export function setSessionExpiredListener(fn) {
  sessionExpiredListener = fn;
}

// Called by the SessionExpiredModal after the user re-authenticates (or on
// unmount) so a subsequent expiry can re-trigger the modal.
export function resetSessionExpiredSignal() {
  sessionExpiredFired = false;
}

function notifySessionExpired() {
  if (BYPASS_AUTH) return;
  if (sessionExpiredFired) return;
  sessionExpiredFired = true;
  if (sessionExpiredListener) {
    try {
      sessionExpiredListener();
    } catch (err) {
      console.error('[apiClient] sessionExpiredListener threw:', err);
    }
  }
}

// Dev-only manual trigger so the modal can be tested without waiting for a
// real token expiry. In production builds `import.meta.env.DEV` is false and
// this block is dead-code-eliminated by Vite.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__triggerSessionExpired = () => {
    console.warn('[apiClient] __triggerSessionExpired called (dev only)');
    sessionExpiredFired = false; // let it re-fire even if a prior real expiry already fired
    notifySessionExpired();
  };
}

// ---------------------------------------------------------------------------
// API Request function (used by dataService)
// ---------------------------------------------------------------------------

export async function apiRequest(endpoint, options = {}) {
  const url = `${API_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add Authorization header if we have a token
  const token = await getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else if (!BYPASS_AUTH) {
    // Do NOT send the request. Every endpoint except /auth/* requires a
    // bearer, so a tokenless request is guaranteed to 401 -- and the pages
    // that call this show an indefinite loading state rather than an error,
    // so the user just sits there. On 2026-10-28 that is exactly what
    // happened: pages fired before the token getter was registered and the
    // app hung on "Loading Product Catalog" with the server correctly
    // answering 401.
    //
    // Failing here instead makes the cause visible in the console and lets
    // the caller's own error handling run.
    const err = new Error('No auth token available; request not sent');
    err.isMissingToken = true;
    console.error(`[apiClient] ${options.method || 'GET'} ${endpoint} - no token, not sending`);
    throw err;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    signal: options.signal,  // Forward AbortController signal for request cancellation
  });

  if (!response.ok) {
    const errorBody = await response.text();
    console.error(`[apiClient] ${options.method || 'GET'} ${endpoint} failed:`, response.status, errorBody);
    if (response.status === 401) {
      notifySessionExpired();
    }
    throw new Error(`API request failed: ${response.status}`);
  }

  // Handle empty responses (204 No Content)
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return null;
}

// ---------------------------------------------------------------------------
// React Hook for initializing token management
// ---------------------------------------------------------------------------

export function useTokenManager() {
  const { getToken: getTokenFromAuth } = useAuth();

  // Registered during RENDER, deliberately, not only in the effect below.
  //
  // React runs effects child-first. useTokenManager lives in App, the parent,
  // so a page's data-fetching effect runs BEFORE App's effect does. While
  // Auth0 existed this was invisible: isLoading started true, every page
  // returned early on its first render, and by the time auth resolved the
  // getter had long been registered. Removing Auth0 made isLoading a constant
  // false, the pages stopped waiting, and their first request went out with no
  // bearer -- see the 401s on 2026-10-28.
  //
  // A parent's render always precedes a child's, so doing it here removes the
  // ordering dependency entirely instead of relying on a timing accident.
  // setTokenGetter only assigns a module-level variable, so this is idempotent
  // and safe under StrictMode's double render.
  if (!BYPASS_AUTH) {
    setTokenGetter(getTokenFromAuth);
  }

  // Kept so a later identity change (new credential after refresh) re-registers.
  useEffect(() => {
    if (BYPASS_AUTH) return;
    setTokenGetter(getTokenFromAuth);
  }, [getTokenFromAuth]);
}
